/**
 * Maintenance-history card content — the facts one log entry states on a phone.
 *
 * The desktop grid gives every entry the same seven columns and prints an em
 * dash wherever the entry has nothing to say. A card has no columns to keep
 * aligned, so an entry contributes only the lines it actually carries: an
 * unresolved issue states who reported it and when, and grows its resolution,
 * technician and resolver lines only once someone closes it out.
 *
 * Contains:
 * - selectMaintenanceHistoryCardMeta — mapped grid row → labelled card entries
 *
 * Returns plain JS — no JSX.
 */

import { selectRowMeta } from '@sophocles/data-grids/components/mobile/cardSections';

/**
 * Builds the labelled metadata lines for one maintenance history card.
 *
 * Labels reuse the keys the desktop columns carry, so a card and a column read
 * the same entry the same way.
 *
 * @param {object} row - mapped grid row ({ reported, reportedBy, resolved,
 *   resolvedBy, technician, resolution }); absent values are null, never a
 *   display placeholder.
 * @param {object} deps
 * @param {Function} deps.t          - i18n translator (key, fallback).
 * @param {Function} deps.formatDate - date value → display string.
 * @returns {{key: string, label: string, value: *}[]} Entries for GridMobileCard's `meta`.
 */
export function selectMaintenanceHistoryCardMeta(row, { t, formatDate }) {
  return selectRowMeta(row, [
    { field: 'reported', label: t('userSettings.reported', 'Reported'), format: formatDate },
    { field: 'reportedBy', label: t('userSettings.reportedBy', 'Reported By') },
    { field: 'resolved', label: t('general.resolved', 'Resolved'), format: formatDate },
    { field: 'resolvedBy', label: t('userSettings.resolvedBy', 'Resolved By') },
    { field: 'technician', label: t('userSettings.technician', 'Technician') },
    { field: 'resolution', label: t('userSettings.resolution', 'Resolution') },
  ]);
}
